import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { parsePastedRows, type PriceTableRowDraft } from "@/lib/price-table-rows";

export interface PasteRowsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApply: (rows: PriceTableRowDraft[], mode: "replace" | "append") => void;
}

export function PasteRowsModal({ open, onOpenChange, onApply }: PasteRowsModalProps) {
  const [text, setText] = useState("");
  const [mode, setMode] = useState<"replace" | "append">("replace");
  const parsed = useMemo(() => parsePastedRows(text), [text]);
  const canApply = text.trim() !== "" && parsed.errors.length === 0 && parsed.rows.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import rows from a spreadsheet</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">
          Columns: Rate type, From, To, Rate1&ndash;Rate9, Range desc (tab or comma separated).
        </p>
        <textarea
          aria-label="Paste rows"
          className="w-full h-40 border rounded p-2 font-mono text-xs"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        {parsed.errors.length > 0 ? (
          <ul className="text-xs text-destructive list-disc pl-4">
            {parsed.errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        ) : (
          <p className="text-xs">
            {parsed.rows.length} row{parsed.rows.length === 1 ? "" : "s"} parsed.
          </p>
        )}
        <div className="flex items-center gap-4 text-sm">
          <label>
            <input
              type="radio"
              checked={mode === "replace"}
              onChange={() => setMode("replace")}
            />{" "}
            Replace all
          </label>
          <label>
            <input
              type="radio"
              checked={mode === "append"}
              onChange={() => setMode("append")}
            />{" "}
            Append
          </label>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!canApply}
            onClick={() => {
              onApply(parsed.rows, mode);
              setText("");
              onOpenChange(false);
            }}
          >
            Apply
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
